import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
  List,
  ListItemButton,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArmazenagemContext } from 'contexts/ArmazenagemContext';
import { useModal, useDialog } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import EtiquetaModal from 'components/Modals/EtiquetaModal';
import Container from 'components/Container';
import Button from 'components/Button';
import Loader from 'components/Loader';
import Header from 'components/Header';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import styles from './styles';
import moment from 'moment';

const Line = ({ label, value, visible = true, large = false, grid = false }) =>
  visible && (
    <Box display="flex" alignItems="center" flex={1}>
      <Typography
        variant="caption"
        fontWeight="bold"
        flex={2}
        textAlign="start"
        color={grid && 'text.secondary'}
      >
        {label}:
      </Typography>
      <Typography
        variant="caption"
        flex={large || grid ? 6 : 3}
        textAlign="start"
        color={grid && 'text.secondary'}
      >
        {value || '-'}
      </Typography>
    </Box>
  );

const ConcluiModal = ({ onSubmit }) => {
  const { closeModal } = useModal();
  const { postLoading } = useContext(ArmazenagemContext);

  return (
    <>
      <DialogTitle>Concluir Armazenagem</DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Icon sx={{ fontSize: 150 }}>check_circle</Icon>
        <Typography variant="h6">
          Deseja realmente concluir o lote de armazenagem?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onSubmit} loading={postLoading}>
          Confirmar
        </Button>
        <Button onClick={closeModal} color="secondary">
          Cancelar
        </Button>
      </DialogActions>
    </>
  );
};

const Detalhes = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [lote, setLote] = useState(null);
  const [selected, setSelected] = useState(null);
  const [itemSelected, setItemSelected] = useState([]);
  const { openDialog } = useDialog();
  const { openModal, closeModal } = useModal();
  const {
    getLoading,
    getLote,
    deleteItensLote,
    deleteLoading,
    postConcluir,
    postLoading,
    deleteItensArm,
  } = useContext(ArmazenagemContext);

  const loadData = () => {
    const cb = (data) => {
      if (Boolean(data)) {
        setLote(() => {
          setSelected(data?.Notas?.[0] || null);
          return data;
        });
      } else {
        navigate(-1);
      }
    };
    closeModal();
    getLote({ id, cb });
  };

  const handleConcluir = () => {
    const data = { id };
    const cb = () => {
      navigate(-1);
      closeModal();
    };
    postConcluir({ data, cb });
  };

  const onDelete = (data) => deleteItensLote({ data, cb: loadData });

  useEffect(loadData, []);

  if (getLoading) {
    return <Loader />;
  }

  return (
    <Container>
      <Header titulo={`Lote ${lote?.documento || ''}`} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {Boolean(lote?.dtemissao) && (
            <Typography variant="body2">
              Emitido em{' '}
              {moment(lote?.dtemissao)?.format('DD/MM/YYYY [às] HH:mm')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="end" gap={2}>
          <Button
            color="primary"
            variant="contained"
            loading={postLoading}
            startIcon={<Icon>check</Icon>}
            onClick={() =>
              openModal(<ConcluiModal onSubmit={handleConcluir} />)
            }
          >
            Concluir Lote
          </Button>
          <Button
            color="secondary"
            variant="contained"
            loading={deleteLoading}
            startIcon={<Icon>delete</Icon>}
            onClick={() =>
              openModal(
                <DeleteModal
                  onSubmit={() =>
                    onDelete(
                      (lote?.Notas || [])?.flatMap((p) =>
                        p?.Itens?.map((pi) => pi?.idlote)
                      )
                    )
                  }
                />
              )
            }
          >
            Excluir Lote
          </Button>
        </Grid>
        <Grid item xs={12} sm={6} md={4} display="flex">
          <Card title="Notas / Faturas" style={styles?.card}>
            {Boolean(lote?.Notas?.length) ? (
              <List>
                {(lote?.Notas || [])?.map((p, i) => (
                  <Box key={`${p?.id}`} display="flex" alignItems="center">
                    <ListItemButton
                      divider={i + 1 < lote?.Notas?.length}
                      selected={selected?.id === p?.id}
                      onClick={() => setSelected(p)}
                    >
                      <Box flex={1}>
                        <Line label="Número" value={p?.documento} />
                        <Line label="Fornecedor" value={p?.cadastro} />
                        <Line
                          label="Data de Emissão"
                          value={
                            Boolean(p?.dtemissao) &&
                            moment(p?.dtemissao)?.format(
                              'DD/MM/YYYY [às] HH:mm'
                            )
                          }
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() =>
                          openModal(
                            <DeleteModal
                              onSubmit={() =>
                                onDelete(p?.Itens?.map((m) => m?.idlote))
                              }
                            />
                          )
                        }
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </ListItemButton>
                  </Box>
                ))}
              </List>
            ) : (
              <Box>
                <Typography variant="caption">
                  Nenhuma nota / fatura encontrada
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={8} display="flex">
          <Card title="Itens" style={styles?.card}>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<Icon>print</Icon>}
              sx={styles?.btn}
              onClick={() => {
                const itens = itemSelected?.length
                  ? selected?.Itens?.filter((f) =>
                      itemSelected?.includes(f?.id)
                    )
                  : selected?.Itens || [];

                return openDialog(
                  <EtiquetaModal itens={itens} armazenagem />,
                  'Imprimir Etiquetas'
                );
              }}
            >
              Imprimir Etiquetas
            </Button>
            <DataGrid
              rows={selected?.Itens || []}
              columns={[
                {
                  field: 'referencia',
                  headerName: 'Referência',
                  width: 150,
                  sortable: false,
                },
                {
                  field: 'descricao',
                  headerName: 'Descrição',
                  minWidth: 300,
                  flex: 1,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <Box flex={1} py={0.5}>
                      <Typography mb={1} fontWeight="bold" textAlign="start">
                        {row?.descricao}
                      </Typography>
                      <Line
                        grid
                        label="Variação"
                        value={row?.variacao}
                        visible={row?.variacao}
                      />
                      <Line
                        grid
                        label="Validade"
                        visible={row?.validade}
                        value={moment(row?.validade)?.format('DD/MM/YYYY')}
                      />
                      <Line
                        grid
                        label="Rastreabilidade"
                        value={row?.rastreabilidade}
                        visible={row?.rastreabilidade}
                      />
                    </Box>
                  ),
                },
                {
                  field: 'quantidade',
                  headerName: 'Quantidade',
                  type: 'number',
                  width: 200,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <Box display="flex" alignItems="center" flex={1} gap={0.5}>
                      <Box flex={1} textAlign="end">
                        <Typography variant="body2">
                          {row?.quantidade} {row?.unidade_medida}
                        </Typography>
                        {Boolean(row?.quantidade_armazenada > 0) && (
                          <Typography variant="caption" fontWeight="bold">
                            Armazenado: {row?.quantidade_armazenada}{' '}
                            {row?.unidade_medida}
                          </Typography>
                        )}
                      </Box>
                      {Boolean(row?.quantidade_armazenada > 0) && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            deleteItensArm({
                              data: row?.Separados?.map((m) => m?.id),
                              cb: loadData,
                            })
                          }
                        >
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      )}
                    </Box>
                  ),
                },
              ]}
              hideFooter
              autoHeight
              density="compact"
              disableSelectionOnClick
              disableColumnMenu
              checkboxSelection
              showCellRightBorder
              showColumnRightBorder
              getRowHeight={() => 'auto'}
              localeText={localeText}
              selectionModel={itemSelected}
              onSelectionModelChange={(ids) => setItemSelected(ids)}
              loading={getLoading || deleteLoading}
            />
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Detalhes;
